import trashIcon from "@assets/icons/ic_trash_gray.svg";
import React, { useRef, useState, DragEvent } from "react";
import { FileText } from "lucide-react";

type BaseUploadElementProps = {
    value: File | null;
    onChange: (file: File | null) => void;
    label?: string;
    buttonText?: string;
    disabled?: boolean;
};

export default function BaseUploadElement({
    value,
    onChange,
    label,
    buttonText,
    disabled = false,
}: BaseUploadElementProps) {
    const inputRef = useRef<HTMLInputElement>(null);
    const [isDragging, setIsDragging] = useState(false);

    const handleFileChange = (file: File | null) => {
        if (!file) return;

        onChange(file);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        handleFileChange(file ?? null);
        e.target.value = "";
    };

    const removeFile = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        onChange(null);
        if (inputRef.current) inputRef.current.value = "";
    };

    // Drag handlers
    const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        if (!disabled) setIsDragging(true);
    };

    const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDrop = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        if (disabled) return;

        const file = e.dataTransfer.files?.[0];
        handleFileChange(file ?? null);
    };

    return (
        <div className="flex flex-col space-y-2">
            {label && <span className="text-base text-[#686868] font-semibold">{label}</span>}
            {!value ? (
                <div
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    className={`flex items-center justify-center border-1 border-dashed rounded-md cursor-pointer ${
                        isDragging ? "border-blue-400 bg-blue-50" : "border-[#E5E5E5] bg-white"
                    }`}
                    onClick={() => inputRef.current?.click()}
                >
                    <span className="text-[#686868] p-3 bg-[#F4F4F4] w-full text-center text-sm h-[56px]">
                        {buttonText}
                    </span>
                </div>
            ) : (
                <div className="flex items-center space-x-2 rounded">
                    <FileText size={20} color="#B1B2E1" />
                    <span className="text-base text-[#3C3769] truncate p-3 text-sm">
                        {value?.name}
                    </span>
                    <button
                        type="button"
                        onClick={removeFile}
                        className="border-none bg-transparent"
                        disabled={disabled}
                    >
                        <img src={trashIcon} alt="" />
                    </button>
                </div>
            )}

            <input
                ref={inputRef}
                type="file"
                className="hidden"
                onChange={handleInputChange}
                disabled={disabled}
            />
        </div>
    );
}
